import { forkJoin } from 'rxjs';
import { Component, OnInit } from '@angular/core';
import { DynamicFormComponent } from '../../../utils/dynamic-form/dynamic-form.component';
import { FieldConfig, FieldType } from '../../../utils/dynamic-form/form.type';
import { ChannelService } from '../../import/service/channel.service';
import { UserService } from '../../user/services/user.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Validators } from '@angular/forms';
import { User } from '../../user/models/user.type';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-add-user-dialog',
  standalone: true,
  imports: [DynamicFormComponent, ButtonModule, TranslateModule, ToastModule],
  templateUrl: './add-user-dialog.component.html',
  styleUrl: './add-user-dialog.component.scss',
  providers: [MessageService, TranslateService]
})
export class AddUserDialogComponent implements OnInit  {
  fields: FieldConfig[] = [];
  channels;
  roles;
  shop;
  languages;
  buttonOutlined = true;
  buttonEnabled = false;
  shops;
  user: User;

  constructor(
    private readonly channelService: ChannelService,
    private readonly userService: UserService,
    private readonly config: DynamicDialogConfig,
    public readonly ref: DynamicDialogRef,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.shop = this.config.data.shop;
    this.user = this.config.data.user;
    forkJoin([
      this.userService.getLanguages(),
      this.userService.getSellerRoles(),
      this.userService.getShops(this.shop.shopCode),
    ]).subscribe({
      next: ([languages, roles, shops]) => {
        this.roles = roles;
        this.languages = languages;
        this.shops = shops;
        this.intFields();
      },
    });
  }

  intFields() {
    const baseFields = [
      {
        name: 'firstname',
        label: 'form.firstname',
        value: this.user?.firstname,
        type: FieldType.input,
        visibleLabel: true,
        placeholder: 'form.firstname',
        alphabeticOnly: true,
        validation: [Validators.required, Validators.pattern(/^\s*\S.*$/)],
        iconPrefix: 'pi pi-user',
      },
      {
        name: 'lastname',
        label: 'form.lastname',
        value: this.user?.lastname,
        type: FieldType.input,
        visibleLabel: true,
        placeholder: 'form.lastname',
        alphabeticOnly: true,
        validation: [Validators.required],
        iconPrefix: 'pi pi-user',
      },
      {
        name: 'phoneNumber',
        label: 'form.phoneNumber',
        value: this.user?.phoneNumber,
        type: FieldType.input,
        placeholder: 'form.phoneNumber',
        visibleLabel: true,
        numericOnly: true,
        validation: [Validators.required],
      },
      {
        name: 'email',
        label: 'form.email',
        value: this.user?.email,
        type: FieldType.input,
        visibleLabel: true,
        placeholder: 'form.email',
        validation: [Validators.email, Validators.required],
        iconPrefix: 'pi pi-envelope',
        disabled: this.user !== undefined
      },
      {
        name: 'password',
        label: 'Mot de passe',
        value: 'Envoyer un lien de réinitialisation',
        type: FieldType.button,
        subType: 'text',
        iconPrefix: 'pi pi-envelope',
        action: 'resetPassword',
        visibleLabel: true,
        visible: this.user === undefined ? 'view' : 'edit'
      },
      {
        name: 'language',
        label: 'form.defaultLanguage',
        value: this.user?.language,
        type: FieldType.select,
        options: this.languages,
        optionLabel: 'languageName',
        visibleLabel: true,
        placeholder: 'form.language',
        validation: [Validators.required]
      }
    ];
  
    // Conditional fields based on user type and context
    const conditionalFields = [];
  
    // Add default role for non-operators
    if (!this.user?.operator) {
      conditionalFields.push({
        name: 'defaultRole',
        label: 'user.defaultRole',
        value: this.user?.defaultRole,
        type: FieldType.select,
        optionLabel: 'roleName',
        options: this.roles,
        visibleLabel: true,
        placeholder: 'form.role',
        validation: [Validators.required]
      });
    }
  
    // Add default shop for new or non-operator users
    if (!this.user) {
      conditionalFields.push({
        name: 'defaultShop',
        label: 'form.defaultShop',
        value: this.user?.defaultShop || this.shops.find((shop) => shop.shopCode === this.shop.shopCode),
        type: FieldType.select,
        options: this.shops.filter(shop => !shop.operator),
        optionLabel: 'shopName',
        visibleLabel: true,
        composite: true,
        validation: [Validators.required]
      });
    }
  
    // Combine base fields with conditional fields
    this.fields = [...baseFields, ...conditionalFields];
  }

  detectFormState(event) {
    const email= this.user?.email;
   
    this.user = event;
    if(this.checkEmailFieldDisabled()){
      this.user.email =email
    }
    this.buttonOutlined = false;
  }

  formChange(event) {
    this.buttonEnabled = event.valid;
  }

  exitDialog() {
    this.ref.close();
  }

  closeDialog() {
    this.ref.close(this.user);
  }

  buttonAction(event) {
    if(event.action == 'resetPassword') {
      this.userService.resetPassword(this.user.email).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('messages.success'),
            detail: this.translate.instant('messages.emailSent'),
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('messages.error'),
            detail: this.translate.instant('messages.errorSummary'),
            life: 3000,
          });
        }
      })
    }
  }

  checkEmailFieldDisabled(): boolean | undefined {
    const emailField = this.fields.find(field => field.name === 'email');
    return emailField?.disabled;
  }
}
